<template>
  <article
    :id="id"
    ref="productCard"
    v-element-visibility="onVisible"
    data-testid="article"
    class="group/product-card relative flex h-full flex-col gap-2 rounded-lg"
    :aria-label="name"
    role="link"
    tabindex="0"
    @mouseover="onMouseOver"
    @mouseleave="onMouseLeave"
  >
    <div
      class="group relative isolate flex aspect-square max-h-md items-center justify-center overflow-hidden rounded-lg bg-white-smoke"
    >
      <div
        v-if="showWishlistToggle"
        class="absolute left-auto right-1 top-2 z-10 flex h-8 w-auto cursor-pointer p-1 transition md:right-0 md:top-0 md:h-12 md:p-3"
      >
        <AsyncSFWishlistToggle
          :product="product"
          :variant="variant"
          :listing-meta-data="listingMetaData"
          :is-carousel-item="isCarouselItem"
          @keydown.enter.stop
        />
      </div>
      <template v-if="link && image">
        <SFProductCardImage
          v-if="shouldShowSingleImage"
          :image="image"
          :alt="alt"
          :link="link"
          :product-index="index"
          @click="handleClick"
        />

        <SFProductCardImageSlider
          v-else
          ref="slider"
          :alt="alt"
          :link="link"
          :is-product-hovered="isProductHovered"
          :product-index="index"
          :images="isMobile ? images.slice(0, 5) : images"
          @click="handleClick"
        />
      </template>
    </div>
    <SFProductCardTileBadges :product="product" />
    <SFProductCardDetails
      v-if="link"
      :show-siblings="showSiblings"
      :product="product"
      :link="link"
      @click.prevent="handleClick"
    />
  </article>
</template>

<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  hydrateOnIdle,
  ref,
  useTemplateRef,
} from 'vue'
import type { Product, Variant } from '@scayle/storefront-nuxt'
import { vElementVisibility } from '@vueuse/components'
import { onKeyStroke, useFocus } from '@vueuse/core'
import SFProductCardImage from './SFProductCardImage.vue'
import SFProductCardImageSlider from './imageSlider/SFProductCardImageSlider.vue'
import SFProductCardDetails from './SFProductCardDetails.vue'
import { useProductBaseInfo, useRouteHelpers } from '~/composables'
import type { ListItem } from '~/types/tracking'
import SFProductCardTileBadges from '~/components/product/card/badges/SFProductCardTileBadges.vue'
import { useDefaultBreakpoints } from '#storefront-ui'

const AsyncSFWishlistToggle = defineAsyncComponent({
  loader: () => import('../SFWishlistToggle.vue'),
  hydrate: hydrateOnIdle(),
})

const {
  product,
  listingMetaData,
  index = -1,
  multipleImages = false,
  showSiblings = true,
  showWishlistToggle = true,
  isCarouselItem = false,
  variant,
} = defineProps<{
  product: Product
  index?: number
  multipleImages?: boolean
  listingMetaData?: ListItem
  hideBadges?: boolean
  showSiblings?: boolean
  showWishlistToggle?: boolean
  isCarouselItem?: boolean
  variant?: Variant
}>()

const hasBeenVisible = ref(false)

const onVisible = (state: boolean) => {
  if (!state) {
    return
  }
  emit('intersect:product')
  hasBeenVisible.value = true
}
const isProductHovered = ref(false)

const onMouseOver = () => {
  isProductHovered.value = true
  emit('product-image:mouseover')
}

const onMouseLeave = () => {
  isProductHovered.value = false
  emit('product-image:mouseleave')
}

const handleClick = () => {
  emit('click-product', link.value)
}

const { alt, image, images, link, name, color, brand } = useProductBaseInfo(
  () => product,
)

const shouldShowSingleImage = computed(() => {
  return !multipleImages || images.value.length === 1 || !hasBeenVisible.value
})

const id = computed(() => `product-${product.id}`)

const emit = defineEmits<{
  'intersect:product': []
  'product-image:mouseover': []
  'product-image:mouseleave': []
  'click-product': [link: string | undefined]
}>()

const productCard = useTemplateRef('productCard')
const slider = useTemplateRef('slider')
const { smaller } = useDefaultBreakpoints()
const isMobile = smaller('md')
const { focused } = useFocus(productCard)
const imageIndex = ref(0)

onKeyStroke(
  ['ArrowLeft', 'ArrowRight'],
  (event: KeyboardEvent) => {
    if (!focused.value || shouldShowSingleImage.value) {
      return
    }
    event.preventDefault()
    imageIndex.value =
      event.code === 'ArrowLeft' ? imageIndex.value - 1 : imageIndex.value + 1
    const imageCount = images.value.length
    slider.value?.scrollImageIntoView(
      ((imageIndex.value % imageCount) + imageCount) % imageCount,
    )
  },
  { target: productCard },
)

const { getProductDetailRoute, localizedNavigateTo } = useRouteHelpers()
onKeyStroke(
  'Enter',
  async () => {
    const route = getProductDetailRoute(
      product.id,
      name.value,
      color.value,
      brand.value,
    )
    await localizedNavigateTo(route)
  },
  {
    target: productCard,
  },
)
</script>
